import {TFunction} from 'i18next';

import {
  GroupActivityItemType,
  IGroupActivityItem,
} from '@/dataSource/src/api/groups/entities';

import {IGroupMember, IGroupParticipant} from '../../models/interfaces';

import {ActivityGroup, Transaction} from './TransactionList';

const formatDateTitle = (isoDate: string, t: TFunction): string => {
  const date = new Date(isoDate);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString())
    return t('groups.dashboard.activities.today');

  if (date.toDateString() === yesterday.toDateString())
    return t('groups.dashboard.activities.yesterday');

  return date.toLocaleDateString('en-US', {month: 'short', day: 'numeric'});
};

const formatAmount = (amount: number): string => `$${amount.toFixed(2)}`;

const buildExpenseTransaction = (
  item: IGroupActivityItem,
  currentParticipant: IGroupParticipant,
  ownerGroupParticipant: IGroupParticipant,
  memberMap: Map<string, IGroupMember>,
): Transaction => {
  const {expense} = item;
  const [payer] = expense.payers ?? [];
  const payerId = payer?.split_user_id ?? expense.owner_id;
  const isCurrentUserPayer = payerId === currentParticipant.splitUserId;
  const ownerMember = memberMap.get(payerId);

  const ownerName = isCurrentUserPayer ? 'You' : ownerMember?.name ?? 'Someone';

  const currentUserParticipant = expense.participants?.find(
    p => p.split_user_id === currentParticipant.splitUserId,
  );

  const total = formatAmount(expense.detail.total);

  const myShare = currentUserParticipant
    ? formatAmount(currentUserParticipant.split_amount)
    : undefined;

  const rightTop = myShare ? 'Your share' : `${ownerName} paid`;

  const avatarLabel = ownerMember?.name.charAt(0).toUpperCase() ?? 'S';

  return {
    id: expense.id,
    title: expense.detail.description || 'Split',
    description: `${ownerName} paid ${total}`,
    rightTop,
    rightBottom: myShare ?? total,
    avatarLabel,
    isOwner: ownerGroupParticipant.splitUserId === expense.owner_id,
  };
};

const buildSettlementTransaction = (
  item: IGroupActivityItem,
  currentParticipant: IGroupParticipant,
  memberMap: Map<string, IGroupMember>,
  t: TFunction,
): Transaction => {
  const {settlement} = item;

  const isCurrentUserPayer = settlement.from === currentParticipant.splitUserId;

  const isCurrentUserReceiver =
    settlement.to === currentParticipant.splitUserId;

  const memberPayer = memberMap.get(settlement.from);
  const memberReceiver = memberMap.get(settlement.to);

  const amount = formatAmount(settlement.amount);

  const title = isCurrentUserReceiver
    ? t('groups.dashboard.activities.receiver', {name: 'You'})
    : t('groups.dashboard.activities.receiver', {
        name: memberReceiver?.name ?? 'Someone',
      });

  const description = isCurrentUserPayer
    ? t('groups.dashboard.activities.payer', {name: 'You'})
    : t('groups.dashboard.activities.payer', {
        name: memberPayer?.name ?? 'Someone',
      });

  return {
    id: settlement.settlement_id,
    title,
    description,
    rightBottom: amount,
    avatarLabel: 'S',
    settled: true,
  };
};

export const mapExpensesToActivityGroups = (
  items: IGroupActivityItem[],
  currentParticipant: IGroupParticipant,
  ownerGroupParticipant: IGroupParticipant,
  members: IGroupMember[],
  t: TFunction,
): ActivityGroup[] => {
  const groupsMap = new Map<string, Transaction[]>();
  const memberMap = new Map(members.map(m => [m.id, m]));

  for (const item of items) {
    const dateTitle = formatDateTitle(item.expense_date, t);
    let transaction: Transaction | null = null;

    switch (item.type) {
      case GroupActivityItemType.EXPENSE:
        transaction = buildExpenseTransaction(
          item,
          currentParticipant,
          ownerGroupParticipant,
          memberMap,
        );
        break;

      case GroupActivityItemType.SETTLEMENT:
        transaction = buildSettlementTransaction(
          item,
          currentParticipant,
          memberMap,
          t,
        );
        break;
    }

    if (!transaction) continue;

    const existing = groupsMap.get(dateTitle) ?? [];
    groupsMap.set(dateTitle, [...existing, transaction]);
  }

  return Array.from(groupsMap.entries()).map(([title, transactions]) => ({
    title,
    transactions,
  }));
};
